import { Modal, Progress } from 'ALIASED-antd';
import React, { useCallback } from 'react';
import { dataTestIdFromTitle } from 'vl-common/src/utils';

type ProgressStatus = 'active' | 'success' | 'exception' | 'normal';

interface UpdateProgressModalProps {
  ProgressModal: {
    destroy: () => void;
    update: (configUpdate: any) => void; // ConfigUpdate type but can't find it
  };
  percentage: number;
  status?: ProgressStatus;
  message?: string;
}

interface Props {
  modalTitle: string;
  initialMessage?: string;
  onSuccessfulCompletion: () => void;
  onFailedCompletion: () => void;
  onSuccessText: string;
  onFailText: string;
}

// New functionality
// Replaces the old Progress Modal
// <ConfirmationModal footer={false} closable={false} open={progress} title={step}>
// <Progress percent={percent} />
// </ConfirmationModal>
const useProgressValidationModal = ({
  modalTitle,
  initialMessage,
  onSuccessfulCompletion,
  onFailedCompletion,
  onSuccessText,
  onFailText
}: Props) => {
  const ProgressBar = useCallback(
    ({ percentage, status = 'active' }: { percentage: number; status?: ProgressStatus }) => {
      return <Progress percent={percentage} status={status} />;
    },
    []
  );

  const updateProgressModal = useCallback(
    ({ ProgressModal, percentage, status = 'active', message }: UpdateProgressModalProps) => {
      const okButtonProps = percentage === 100 ? null : { style: { display: 'none' } };
      const cancelButtonProps = status === 'success' ? null : { style: { display: 'none' } };
      const cancelText = status === 'success' ? 'Close' : null;
      const onOk =
        status === 'success'
          ? () => {
              ProgressModal.destroy();
              onSuccessfulCompletion();
            }
          : () => {
              ProgressModal.destroy();
              onFailedCompletion();
            };
      const okText = status === 'success' ? onSuccessText : onFailText;
      ProgressModal.update({
        okButtonProps,
        cancelButtonProps,
        okText,
        onOk,
        cancelText,
        closable: status === 'success',
        content: (
          <>
            <ProgressBar percentage={percentage} status={status} />
            {message && <p>{message}</p>}
          </>
        )
      });
    },
    [ProgressBar, onFailText, onFailedCompletion, onSuccessText, onSuccessfulCompletion]
  );

  const loadProgressValidationModal = () => {
    return Modal.confirm({
      title: modalTitle,
      modalRender: (originalNode) => <div data-testid={dataTestIdFromTitle(modalTitle)}>{originalNode}</div>,
      okButtonProps: { style: { display: 'none' } },
      cancelText: null,
      centered: true,
      content: (
        <>
          <ProgressBar percentage={0} />
          <p>{initialMessage}</p>
        </>
      )
    });
  };

  return { loadProgressValidationModal, updateProgressModal };
};

export default useProgressValidationModal;
